// DocumentGenerator.vue
<template>
  <b-modal
    :id="modalId"
    :ref="modalRef"
    size="xl"
    no-close-on-backdrop
    :modal-class="[modalClass, 'enhanced-modal']"
    :ok-title="documentConfig.buttons.print"
    :cancel-title="documentConfig.buttons.clear"
    cancel-variant="danger"
    content-class="modal-content-custom"
  >
    <!-- Enhanced modal title -->
    <template #modal-title>
      <div class="modal-title-custom">
        <h5 class="mb-0 d-flex align-items-center">
          <span class="title-icon mr-2">
            <i class="fas fa-file-medical"></i>
          </span>
          {{ documentConfig.title }}
          <span v-if="documentConfig.subtitle" class="subtitle-badge ml-2">
            {{ documentConfig.subtitle }}
          </span>
        </h5>
      </div>
    </template>

    <!-- Two-Column Layout for Better UX -->
    <div class="container-fluid px-0">
      <b-row>
        <!-- Left Column - Form Inputs -->
        <b-col lg="5" class="pr-lg-3">
          <!-- Print options (medical certificate only) -->
          <div v-if="documentType === 'medicalCert'" class="mc-options mb-3">
            <div class="mc-option-row">
              <span class="mc-option-label">Language</span>
              <b-button-group size="sm">
                <b-button
                  :variant="lang === 'th' ? 'dark' : 'outline-secondary'"
                  @click="lang = 'th'"
                >TH</b-button>
                <b-button
                  :variant="lang === 'en' ? 'dark' : 'outline-secondary'"
                  @click="lang = 'en'"
                >EN</b-button>
              </b-button-group>
            </div>

            <div class="mc-option-row">
              <span class="mc-option-label">โลโก้</span>
              <b-button-group size="sm">
                <b-button
                  :variant="showLogo ? 'dark' : 'outline-secondary'"
                  @click="showLogo = true"
                >แสดง</b-button>
                <b-button
                  :variant="!showLogo ? 'dark' : 'outline-secondary'"
                  @click="showLogo = false"
                >ซ่อน</b-button>
              </b-button-group>
            </div>

            <div class="mc-option-row">
              <span class="mc-option-label">หัวกระดาษ</span>
              <b-button-group size="sm">
                <b-button
                  :variant="headerFormat === 'clinic_branch' ? 'dark' : 'outline-secondary'"
                  @click="headerFormat = 'clinic_branch'"
                >คลินิก + สาขา</b-button>
                <b-button
                  :variant="headerFormat === 'clinic' ? 'dark' : 'outline-secondary'"
                  @click="headerFormat = 'clinic'"
                >คลินิก</b-button>
                <b-button
                  :variant="headerFormat === 'branch' ? 'dark' : 'outline-secondary'"
                  @click="headerFormat = 'branch'"
                >สาขา</b-button>
              </b-button-group>
            </div>
          </div>

          <!-- Patient Info -->
          <b-card v-if="documentConfig.showPatientName" class="mb-3 border-0 shadow-sm">
            <b-card-header class="bg-primary text-white border-0">
              <i class="fas fa-user-circle mr-2"></i>
              <strong>Patient Information</strong>
            </b-card-header>
            <b-card-body>
              <div class="mb-2">
                <small class="text-muted d-block">{{ documentConfig.labels.patientName }}</small>
                <div class="font-weight-medium">{{ ptFullname }}</div>
              </div>
              <div v-if="ptAddress && documentConfig.showAddress">
                <small class="text-muted d-block">{{ documentConfig.labels.address }}</small>
                <div class="font-weight-medium">{{ ptAddress }}</div>
              </div>
            </b-card-body>
          </b-card>

          <!-- Doctor Information Form -->
          <b-card 
            v-if="documentConfig.showDoctorTitle || documentConfig.showDoctorSelect || documentConfig.showComments"
            class="mb-3 border-0 shadow-sm"
          >
            <b-card-header class="bg-success text-white border-0">
              <i class="fas fa-user-md mr-2"></i>
              <strong>Doctor Information</strong>
            </b-card-header>
            <b-card-body>
              <b-form>
                <b-form-group 
                  v-if="documentConfig.showDoctorTitle"
                  :label="documentConfig.labels.doctorTitle"
                  label-size="sm"
                  class="mb-3"
                >
                  <b-form-select
                    v-model="$v.title.$model"
                    :options="titleOptions"
                    :state="$v.title.$error ? false : null"
                    size="sm"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null" disabled>
                        {{ documentConfig.placeholders.selectTitle }}
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>
                
                <b-form-group 
                  v-if="documentConfig.showDoctorSelect"
                  :label="documentConfig.labels.doctor"
                  label-size="sm"
                  class="mb-3"
                >
                  <b-form-select
                    v-model="$v.selectId.$model"
                    :options="doctorList"
                    text-field="fullName"
                    value-field="uid"
                    :state="$v.selectId.$error ? false : null"
                    size="sm"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null" disabled>
                        {{ documentConfig.placeholders.selectDoctor }}
                      </b-form-select-option>
                    </template>
                  </b-form-select>
                </b-form-group>

                <b-form-group 
                  v-if="documentConfig.showComments"
                  :label="documentConfig.labels.comments"
                  label-size="sm"
                >
                  <b-form-textarea
                    v-model="commentDetail"
                    :placeholder="documentConfig.placeholders.comments"
                    rows="3"
                    size="sm"
                  ></b-form-textarea>
                </b-form-group>
              </b-form>
            </b-card-body>
          </b-card>

        </b-col>

        <!-- Right Column - Treatment Table -->
        <b-col lg="7" class="pl-lg-3">
          <b-card v-if="documentConfig.showTreatmentTable" class="h-100 border-0 shadow-sm">
            <b-card-header class="bg-info text-white border-0 d-flex justify-content-between align-items-center">
              <div>
                <i class="fas fa-table mr-2"></i>
                <strong>{{ documentConfig.labels.tableCaption }}</strong>
              </div>
              <b-badge v-if="selectedItem.length > 0" variant="light" class="ml-2">
                {{ selectedItem.length }} selected
              </b-badge>
            </b-card-header>
            <b-card-body class="p-0 position-relative" style="min-height: 400px;">
              <div v-if="treatmentItems.length === 0" class="text-center py-5 text-muted">
                <i class="fas fa-inbox fa-3x mb-3 text-muted"></i>
                <p>No treatment history available</p>
              </div>
              <b-table
                v-else
                small
                striped
                hover
                responsive
                sticky-header="350px"
                sort-icon-left
                sort-by="creationDt"
                sort-desc
                :items="treatmentItems"
                :fields="tableHeaders"
                select-mode="multi"
                selectable
                @row-selected="onRowSelected"
                class="mb-0"
              >
                <template #cell(selected)="{ rowSelected }">
                  <i v-if="rowSelected" class="fas fa-check-circle text-success"></i>
                  <i v-else class="far fa-circle text-muted"></i>
                </template>
              </b-table>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- Print Preview Section -->
    <div class="d-none">
      <div ref="header">
        <template v-if="documentType !== 'medicalCert'">
          <b-row>
            <b-col cols="2" class="text-center">
              <b-img fluid :src="logo" :alt="clinic.name" style="height: 95px" />
            </b-col>
            <b-col>
              <h4 class="mb-1">{{ clinic.name }}</h4>
              <p v-if="branchInfo && branchInfo.address" class="mb-1 my-justify">
                {{ branchInfo.address }}
              </p>
              <p class="mb-1">
                <span v-if="branchInfo && branchInfo.tel">
                  {{ documentConfig.labels.tel }}: {{ branchInfo.tel }}
                </span>
                <span v-if="branchInfo && branchInfo.line" class="ml-3">
                  Line ID: {{ branchInfo.line }}
                </span>
              </p>
              <p v-if="branchInfo && branchInfo.website" class="mb-1">
                {{ branchInfo.website }}
              </p>
            </b-col>
          </b-row>
          <hr />
        </template>
      </div>

      <!-- Document Body -->
      <div ref="body">
        <!-- Medical Certificate Template -->
        <div v-if="documentType === 'medicalCert'">
          <Medical-certTemplate
            :document-data="{
              title,
              doctorObj,
              ptFullname,
              ptFullnameEn,
              patient,
              selectedItem,
              groupSelectedItem,
              commentDetail,
              today,
              todayEn,
              lang,
              showLogo,
              headerFormat,
              logoUrl: logo,
            }"
          />
        </div>

        <div v-if="documentType === 'dentalImplantConsent'">
          <DentalImplantConsentForm
            :document-data="{
              title,
              doctorObj,
              ptFullname,
              patient,
              commentDetail,
              today,
            }"
          />
        </div>

        <div v-if="documentType === 'referralLetter'">
          <ReferralLetterTemplate
            :document-data="{
              title,
              doctorObj,
              ptFullname,
              patient,
              selectedItem,
              groupSelectedItem,
              commentDetail,
              today,
            }"
          />
        </div>

        <div v-if="documentType === 'marketingConsent'">
          <MarketingConsentForm
            :document-data="{
              title,
              doctorObj,
              ptFullname,
              patient,
              commentDetail,
              consentDate: today,
              marketingConsent: 'no_consent',
            }"
          />
        </div>

        <div v-if="documentType === 'veneerConsent'">
          <VeneerConsentForm
            :document-data="{
              title,
              doctorObj,
              ptFullname,
              patient,
              commentDetail,
              consentDate: today,
            }"
          />
        </div>

        <div v-if="documentType === 'traditionalBracesConsent'">
          <TraditionalBracesConsentForm
            :document-data="{
              title,
              doctorObj,
              ptFullname,
              patient,
              commentDetail,
              consentDate: today,
            }"
          />
        </div>

        <div v-if="documentType === 'clearAlignerConsent'">
          <ClearAlignerConsentForm
            :document-data="{
              title,
              doctorObj,
              ptFullname,
              patient,
              commentDetail,
              consentDate: today,
            }"
          />
        </div>
      </div>
    </div>

    <!-- Modal Footer with Action Buttons -->
    <template #modal-footer>
      <div class="w-100 d-flex justify-content-between align-items-center">
        <small class="text-muted">
          <i class="fas fa-info-circle mr-1"></i>
          Select treatments and fill required fields before generating
        </small>
        <div>
          <b-button
            v-if="documentConfig.showUpload"
            variant="outline-info"
            class="mr-2"
            :disabled="!canGenerateDocument"
            @click="uploadPdf"
            size="sm"
          >
            <i class="fas fa-signature mr-2"></i>
            {{ documentConfig.buttons.upload }}
          </b-button>
          <b-button 
            variant="primary" 
            @click="generatePdf"
            size="sm"
          >
            <i class="fas fa-print mr-1"></i>
            {{ documentConfig.buttons.print }}
          </b-button>
        </div>
      </div>
    </template>

    <PdfExport ref="pdfExport" @openSignTabCompleted="hideModal" @showSignDialog="handleShowSignDialog" />
    <Dialog ref="Dialog" />
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import Dialog from "@/components/modal/Dialog";
import PdfExport from "@/components/PdfExport";
import moment from "moment";
import { required } from "vuelidate/lib/validators";
import { documentConfig } from "./config/documentConfig";
import { FEATURE } from "@/constants/entitlements";

import MedicalCertTemplate from "./templates/MedicalCertTemplate.vue";
import DentalImplantConsentForm from "./templates/DentalImplantConsentForm.vue";
import ReferralLetterTemplate from "./templates/ReferralLetterTemplate.vue";
import MarketingConsentForm from "./templates/MarketingConsentForm.vue";
import VeneerConsentForm from "./templates/VeneerConsentForm.vue";
import TraditionalBracesConsentForm from "./templates/TraditionalBracesConsentForm.vue";
import ClearAlignerConsentForm from "./templates/ClearAlignerConsentForm.vue";

export default {
  name: "DocumentGenerator",
  components: {
    PdfExport,
    Dialog,
    MedicalCertTemplate,
    DentalImplantConsentForm,
    ReferralLetterTemplate,
    MarketingConsentForm,
    VeneerConsentForm,
    TraditionalBracesConsentForm,
    ClearAlignerConsentForm,
  },

  props: {
    documentType: {
      type: String,
      required: true,
      // validator: function (value) {
      //   return (
      //     [
      //       "empty",
      //       "medicalCert",
      //       "dentalImplantConsent",
      //       "referralLetter",
      //     ].indexOf(value) !== -1
      //   );
      // },
    },
    modalId: {
      type: String,
      default: "document-modal",
    },
    modalRef: {
      type: String,
      default: "documentModal",
    },
    modalClass: {
      type: String,
      default: "component-modal-document",
    },
  },

  data() {
    return {
      patient: {},
      clinic: {},
      logo: null,
      titleOptions: ["ทพ.", "ทพญ."],
      title: null,
      selectId: null,
      commentDetail: "",
      selectedItem: [],
      isPdfCreating: false,
      lang: "th",
      showLogo: true,
      headerFormat: "clinic_branch",
      tableHeaders: [
        {
          key: "selected",
          label: "",
          class: "text-center",
          thStyle: "min-width: 32px",
        },
        {
          label: "วันที่ตรวจ/รักษา",
          sortable: true,
          key: "creationDt",
          formatter: this.formatDate,
        },
        {
          label: "ซี่ฟัน",
          key: "toothNumberSurfaces",
          formatter: function (value) {
            return value || "";
          },
        },
        {
          label: "วินิจฉัย",
          key: "dxName",
        },
        {
          label: "รายการ",
          key: "name",
        },
        {
          label: "แพทย์",
          key: "doctorName",
        },
      ],
    };
  },

  validations() {
    const validations = {};

    if (this.documentConfig.showDoctorTitle) {
      validations.title = { required };
    }

    if (this.documentConfig.showDoctorSelect) {
      validations.selectId = { required };
    }

    return validations;
  },

  computed: {
    ...mapGetters({
      branchInfo: "moduleUser/getBranchInfo",
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
      getTreatDiagBy: "moduleAppointment/getTreatDiagBy",
      getDoctorList: "moduleUser/getDoctorList",
      getUserList: "moduleUser/getUserList",
      getUserListIncludeDeletedBy: "moduleUser/getUserListIncludeDeletedBy",
      userInfo: "moduleUser/getUserInfo",
      isDoctor: "moduleUser/isDoctor",
      viewFile: "moduleContent/viewFile",
      entitlementFlag: "moduleBranch/entitlementFlag",
    }),

    canGenerateDocument() {
      return this.entitlementFlag(FEATURE.DOCUMENT_GENERATOR);
    },

    documentConfig() {
      return documentConfig[this.documentType] || {};
    },

    today() {
      return moment().locale("th").add(543, "years").format("D MMMM YYYY");
    },

    todayEn() {
      return moment().locale("en").format("D MMMM YYYY");
    },

    ptFullname() {
      return [
        this.patient.titleNameTh,
        this.patient.firstNameTh,
        this.patient.lastNameTh,
      ]
        .filter(Boolean)
        .join(" ");
    },

    ptFullnameEn() {
      const en = [
        this.patient.titleNameEn,
        this.patient.firstNameEn,
        this.patient.lastNameEn,
      ]
        .filter(Boolean)
        .join(" ");
      return en || this.ptFullname;
    },

    ptAddress() {
      const { address, subDistrict, district, province, country, postcode } =
        this.patient;
      return [address, subDistrict, district, province, country, postcode]
        .filter(Boolean)
        .join(" ");
    },

    doctorList() {
      return this.getDoctorList.map(function (doctor) {
        var user = this.getUserList.find(function (user) {
          return user.uid === doctor.uid;
        });
        return Object.assign({}, doctor, {
          drLicenseNo: user ? user.drLicenseNo : null,
        });
      }, this);
    },

    doctorObj() {
      return this.doctorList.find(doctor => this.selectId === doctor.uid) || {};
    },

    treatmentItems() {
      return this.getTreatmentHistoryList
        .map(function (item) {
          return item.items;
        })
        .flat()
        .filter(function (item) {
          return [1, 2].includes(item.typeId);
        })
        .map(item => ({
          ...item,
          doctorName: this.getUserListIncludeDeletedBy(item.actionUid, "uid")
            .fullName,
          dxName: this.getTreatDiagBy(item.diagItems, "id").nameEn,
          toothNumberSurfaces: `${item.toothItems || ""} ${this.formatSurfaces(
            item
          )}`,
        }));
    },

    groupSelectedItem() {
      const sorted = this.selectedItem
        .slice(0)
        .sort((a, b) => Date.parse(a.creationDt) - Date.parse(b.creationDt))
        .map(item => ({
          ...item,
          formattedDate: this.formatDate(item.creationDt),
        }));

      return sorted.reduce((acc, item) => {
        const date = item.formattedDate;
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(item);
        return acc;
      }, {});
    },
  },

  methods: {
    show(patient) {
      this.$refs[this.modalRef].show();
      this.clinic = this.branchInfo.clinic;
      this.patient = patient;
      this.logo = this.viewFile(this.branchInfo.clinic.logo) || null;
    },
    hideModal() {
      this.$refs[this.modalRef].hide();
    },

    onRowSelected(items) {
      this.selectedItem = items;
    },

    async uploadPdf(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.isPdfCreating = true;
      
      // Set up user interaction context for iPad
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      // Create a user-interaction context that can be used later
      let userInteractionContext = null;
      if (isIOS) {
        // Store the current user interaction context
        userInteractionContext = {
          timestamp: Date.now(),
          event: bvModalEvt,
        };
        // Pass this context to PdfExport component
        this.$refs.pdfExport.userInteractionContext = userInteractionContext;
      }

      try {
        await this.$nextTick();
        
        await this.$refs.pdfExport.generatePdf({
          header: this.$refs.header,
          body: this.$refs.body,
          footer: this.$refs.footer,
          option: {
            isUpload: true,
            patientId: this.patient.id,
            remarkTitle: this.documentConfig.filePrefix + "_",
          },
        });
      } finally {
        this.isPdfCreating = false;
        this.$v.$reset();
      }
    },

    async generatePdf(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.$v.$touch();
      // Only validate if there are validation rules
      if (Object.keys(this.$v.$params).length > 0) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      }
      this.isPdfCreating = true;

      try {
        await this.$nextTick();
        await this.$refs.pdfExport.generatePdf({
          header: this.$refs.header,
          body: this.$refs.body,
          footer: this.$refs.footer,
        });
      } finally {
        this.isPdfCreating = false;
        this.$v.$reset();
      }
    },

    formatDate(date) {
      return moment(date).add(543, "years").locale("th").format("D MMMM YYYY");
    },

    formatSurfaces(item) {
      var surfaces = [
        { id: 5, label: "O", order: 1 },
        { id: 3, label: "M", order: 2 },
        { id: 4, label: "D", order: 3 },
        { id: 1, label: "B", order: 4 },
        { id: 2, label: "Li", order: 5 },
      ];

      if (!item.surfaceItems) return "";

      return item.surfaceItems
        .map(function (surfaceId) {
          return surfaces.find(function (s) {
            return s.id === surfaceId;
          });
        })
        .sort(function (a, b) {
          return a.order - b.order;
        })
        .map(function (surface) {
          return surface.label;
        })
        .join("");
    },

    calAge(dateString) {
      if (!dateString) return;
      const date = moment(dateString, "YYYY-MM-DD");
      return moment().diff(date, "years");
    },
    
    handleShowSignDialog(data) {
      // Show a user-friendly modal for iPad users to manually open signing page
      this.$refs.Dialog.showAlertConfirm(
        data.title,
        `${data.message}\n\nกรุณาแตะปุ่มด้านล่างเพื่อไปยังหน้าเซ็นต์เอกสาร`,
        {
          okTitle: 'เปิดหน้าเซ็นต์เอกสาร',
          cancelTitle: 'ยกเลิก'
        }
      ).then((result) => {
        if (result.isConfirmed) {
          // User clicked confirm, open the signing page
          window.open(data.url, '_blank', 'noopener,noreferrer');
        }
      });
    },
  },

  mounted() {
    if (this.isDoctor) {
      this.selectId = this.userInfo.uid;
    }

    const sl = localStorage.getItem("medicalCertShowLogo");
    if (sl !== null) this.showLogo = sl === "true";

    const hf = localStorage.getItem("medicalCertHeaderFormat");
    if (hf && ["clinic_branch", "clinic", "branch"].includes(hf)) {
      this.headerFormat = hf;
    }
  },

  watch: {
    showLogo(v) {
      localStorage.setItem("medicalCertShowLogo", String(v));
    },
    headerFormat(v) {
      localStorage.setItem("medicalCertHeaderFormat", v);
    },
  },
};
</script>

<style lang="scss" scoped>
.enhanced-modal {
  .modal-title-custom {
    .subtitle-badge {
      background: #dc3545;
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 50px;
      font-size: 0.875rem;
    }
  }

  .modal-dialog {
    max-width: 1200px;
  }
}

// Enhanced card styling
.card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.1) !important;
  }
}

.font-weight-medium {
  font-weight: 500;
}

// Responsive adjustments
@media (max-width: 991px) {
  .modal-dialog {
    margin: 0.5rem;
  }
}

.mc-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.8rem;
}

.mc-option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  .mc-option-label {
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 500;
    min-width: 90px;
  }
}
</style>